import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Upload as UploadIcon,
  Collections as CollectionsIcon,
  Image as ImageIcon,
  AttachMoney as MoneyIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';
import axios from 'axios';

const Attachments = () => {
  const { t } = useLocalization();
  const { token } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [baseBonus, setBaseBonus] = useState(0);
  const [bonusWeight, setBonusWeight] = useState(1.0);
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    fetchAttachments();
  }, []);

  // Cleanup image URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/attachments/available', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttachments(response.data);
      setError(null);

      // Load images for each attachment
      const newImageUrls = {};
      for (const attachment of response.data) {
        try {
          const imageResponse = await axios.get(`/attachments/image/${attachment.attachment_id}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          newImageUrls[attachment.attachment_id] = URL.createObjectURL(imageResponse.data);
        } catch (err) {
          console.error(`Error loading image for attachment ${attachment.attachment_id}:`, err);
        }
      }
      setImageUrls(newImageUrls);
    } catch (err) {
      setError(t('failed_to_fetch_attachments'));
      console.error('Error fetching attachments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t('please_select_file_first'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('base_bonus', baseBonus);
      formData.append('bonus_weight', bonusWeight);

      const response = await axios.post('/attachments/load', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(t('attachment_uploaded_successfully'));
      setSelectedFile(null);
      setBaseBonus(0);
      setBonusWeight(1.0);
      fetchAttachments();
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_upload_attachment'));
      console.error('Error uploading attachment:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalBonus = attachments.reduce((sum, attachment) => sum + (attachment.base_bonus || 0), 0);
  const avgWeight = attachments.length > 0 
    ? attachments.reduce((sum, attachment) => sum + (attachment.bonus_weight || 0), 0) / attachments.length 
    : 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <CollectionsIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('attachments_management')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('upload_manage_game_attachments')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={12}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <CollectionsIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {attachments.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_attachments')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Upload Section */}
      <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ 
              width: 50, 
              height: 50, 
              mr: 2, 
              bgcolor: 'primary.main'
            }}>
              <UploadIcon sx={{ fontSize: 25 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('upload_new_attachment')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('add_new_images_bonus_configurations')}
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ 
                  borderRadius: 2,
                  py: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {selectedFile ? selectedFile.name : t('select_file')}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('base_bonus')}
                type="number"
                value={baseBonus}
                onChange={(e) => setBaseBonus(Number(e.target.value))}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                helperText={t('bonus_value_for_attachment')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('bonus_weight')}
                type="number"
                value={bonusWeight}
                onChange={(e) => setBonusWeight(Number(e.target.value))}
                fullWidth
                inputProps={{ step: 0.1 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                helperText={t('weight_multiplier_bonus_calculation')}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || loading}
                fullWidth
                sx={{ 
                  borderRadius: 2,
                  py: 2,
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('upload_attachment')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Attachments Grid */}
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ 
              width: 50, 
              height: 50, 
              mr: 2, 
              bgcolor: 'primary.main'
            }}>
              <CollectionsIcon sx={{ fontSize: 25 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('available_attachments')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('browse_manage_uploaded_attachments')}
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />

          {attachments.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: 200,
              color: 'text.secondary',
              fontSize: '1rem',
              fontWeight: 500
            }}>
              {t('no_attachments_found')}
            </Box>
          ) : (
            <Grid container spacing={3}>
              {attachments.map((attachment) => (
                <Grid item xs={12} sm={6} md={4} key={attachment.attachment_id}>
                  <Card sx={{ 
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={imageUrls[attachment.attachment_id]}
                      alt={`Attachment ${attachment.attachment_id}`}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            label={`${attachment.base_bonus || 0} ${t('bonus_text')}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                          <Chip
                            icon={<AutoAwesomeIcon />}
                            label={`${attachment.bonus_weight || 1.0}${t('weight_text')}`}
                            color="secondary"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Attachments; 